import { AppIcon } from '@/components/ui/app-icon';
import { useEffect, useRef, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { iconGear } from '@/lib/icons';
import { BRAND } from '@/lib/brand';

/**
 * Global ops toggle (100% rollout, no targeting) shared with the backend's
 * MaintenanceModeMiddleware. Turn it on in PostHog before a DB migration or
 * other downtime: the API 503s and every portal tab swaps to this screen.
 */
export const MAINTENANCE_FLAG = 'maintenance_mode_v1';
const RECHECK_MS = 30_000;

export const MaintenanceGate = ({ children }: { children: ReactNode }) => {
  const on = useFeatureFlagEnabled(MAINTENANCE_FLAG) === true;
  const wasOn = useRef(false);

  useEffect(() => {
    if (on) {
      wasOn.current = true;
      // posthog-js only fetches flags on load, so poll while down to notice
      // the flag going off without the user reloading.
      const id = setInterval(() => posthog.reloadFeatureFlags(), RECHECK_MS);
      return () => clearInterval(id);
    }
    // Back up: reload so queries that 503'd and chunks from before the
    // deploy don't linger.
    if (wasOn.current) window.location.reload();
  }, [on]);

  if (!on) return children;

  return (
    <div
      role='alert'
      className='fixed inset-0 z-9999 flex items-center justify-center bg-background p-6'
    >
      <div className='flex max-w-sm flex-col items-center text-center'>
        <div className='mb-5 flex h-12 w-12 items-center justify-center rounded-[10px] border border-border bg-muted'>
          <AppIcon
            icon={iconGear}
            className='h-6 w-6 animate-spin text-primary [animation-duration:3s] motion-reduce:animate-none'
          />
        </div>
        <h1 className='mb-2 text-lg font-medium text-foreground'>
          We&apos;ll be right back
        </h1>
        <p className='mb-5 text-sm leading-relaxed text-muted-foreground'>
          {BRAND.name} is undergoing scheduled maintenance. Your data is safe,
          and this page will refresh on its own when we&apos;re back.
        </p>
        <p className='text-xs text-muted-foreground'>
          Questions?{' '}
          <a
            href='mailto:support@cleanlist.ai'
            className='font-medium text-primary hover:underline'
          >
            support@cleanlist.ai
          </a>
        </p>
      </div>
    </div>
  );
};
